if (!customElements.get('subscription-frequency-selector')) {
  customElements.define(
    'subscription-frequency-selector',
    class SubscriptionFrequencySelector extends HTMLElement {
      /**
       * On the product page a Recharge widget is mounted alongside this one and
       * injects the selling_plan input itself, so we wait and adopt it. In a
       * product card there is no Recharge widget at all — nothing will ever
       * inject one — so the element is told to own the input from the start via
       * the data-owns-input attribute. Waiting there would just mean four dead
       * seconds where Add to cart submits without a selling plan.
       */
      get ownsInput() {
        return this.hasAttribute('data-owns-input');
      }

      connectedCallback() {
        // This element is rendered before the buy_buttons block, so at
        // upgrade time the product <form> has not been parsed yet and
        // getElementById would return null. Wait for the document instead
        // of bailing out. When injected into a quick-add modal after load,
        // readyState is already past 'loading' and init runs immediately.
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
        } else {
          this.init();
        }
      }

      init() {
        if (this.initialized) return;
        this.initialized = true;

        this.form = document.getElementById(this.dataset.formId);
        this.planInputs = Array.from(this.querySelectorAll('[data-sfs-plan-input]'));
        this.modeInputs = Array.from(this.querySelectorAll('[data-sfs-mode]'));
        this.frequencyLabel = this.querySelector('[data-sfs-frequency]');

        // The theme's price block. price.liquid knows nothing about selling
        // plans — it renders variant.price and stops — so the page would keep
        // showing the one-time price no matter what is selected. The original
        // markup is kept verbatim and restored wholesale for one-time, rather
        // than being edited back, so nothing can drift out of sync with it.
        this.priceTarget = this.dataset.priceTarget
          ? document.getElementById(this.dataset.priceTarget)
          : null;
        if (this.priceTarget) this.priceOriginalHTML = this.priceTarget.innerHTML;

        if (!this.form || !this.planInputs.length) {
          console.warn(
            '[subscription-frequency-selector] inert:',
            this.form ? 'no frequency inputs rendered' : `no form #${this.dataset.formId}`
          );
          return;
        }

        this.selectedPlanId = this.dataset.defaultPlanId || this.planInputs[0].value;

        // Purchase type is a two-radio group rather than a checkbox, so read the
        // checked one. Subscription is the markup default; falling back to true
        // keeps that the default if neither radio is checked for any reason.
        const checkedMode = this.modeInputs.find((input) => input.checked);
        this.subscribed = checkedMode ? checkedMode.value === 'subscription' : true;

        this.planInputs.forEach((input) => {
          input.addEventListener('change', this.onPlanChange.bind(this));
        });

        this.modeInputs.forEach((input) => {
          input.addEventListener('change', this.onModeChange.bind(this));
        });

        if (this.ownsInput) {
          if (!this.sellingPlanInput()) this.createFallbackInput();
        } else {
          // The selling_plan input is injected at runtime by Recharge, so it
          // may not exist yet on first paint. Watch the form for it arriving
          // and apply our state onto it as soon as it does. This observer is
          // for *finding* the input, not for policing its value — it only
          // reacts when the input node itself changes identity, so a value
          // Recharge writes later is left alone (and stays observable).
          this.observer = new MutationObserver(() => {
            if (this.sellingPlanInput() !== this.trackedInput) this.apply();
          });
          this.observer.observe(this.form, { childList: true, subtree: true });

          // Last resort: if Recharge never injects one (widget failed to
          // load, block removed in the editor), own the input ourselves so
          // the custom widget still works.
          this.fallbackTimer = setTimeout(() => {
            if (!this.sellingPlanInput()) this.createFallbackInput();
            this.apply();
          }, 4000);
        }

        this.render();
        this.apply();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
      }

      onPlanChange(event) {
        this.selectedPlanId = event.target.value;
        // Picking a frequency implies the customer wants a subscription. The
        // list is collapsed while one-time is selected, so this is mostly a
        // safety net for a frequency reached some other way.
        if (!this.subscribed) this.subscribed = true;
        this.render();
        this.apply();
      }

      onModeChange(event) {
        this.subscribed = event.target.value === 'subscription';
        this.render();
        this.apply();
      }

      /** The one field that actually matters — Recharge's, if it exists. */
      sellingPlanInput() {
        const inputs = Array.from(this.form.querySelectorAll('input[name="selling_plan"]'));
        if (inputs.length < 2) return inputs[0] || null;
        // Recharge injected its own after we made a fallback: drop ours so
        // the form never submits two selling_plan values.
        const native = inputs.find((input) => !input.hasAttribute('data-sfs-fallback'));
        if (native) {
          inputs.forEach((input) => {
            if (input !== native) input.remove();
          });
          return native;
        }
        return inputs[0];
      }

      createFallbackInput() {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        input.setAttribute('data-sfs-fallback', '');
        this.form.appendChild(input);
        return input;
      }

      /** Writes the current state into the form field. */
      apply() {
        const input = this.sellingPlanInput();
        if (!input) return;
        this.trackedInput = input;

        if (this.subscribed) {
          // Disabled inputs are excluded from FormData, so re-enabling is
          // what puts selling_plan back into the /cart/add.js payload.
          input.disabled = false;
          input.value = this.selectedPlanId;
        } else {
          input.disabled = true;
        }

        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /** Visual state only. */
      render() {
        // These two classes drive everything visual: which purchase-type box
        // takes the accent, and whether the frequency list is slid open.
        this.classList.toggle('sfs--subscribed', this.subscribed);
        this.classList.toggle('sfs--onetime', !this.subscribed);

        // Keeps the radios honest when state was changed in code rather than by
        // a click — onPlanChange flipping the mode, for instance.
        const mode = this.subscribed ? 'subscription' : 'onetime';
        this.modeInputs.forEach((input) => {
          if (input.value === mode && !input.checked) input.checked = true;
        });

        const selected = this.planInputs.find((input) => input.value === this.selectedPlanId);
        if (selected && !selected.checked) selected.checked = true;
        if (selected && this.frequencyLabel) {
          this.frequencyLabel.textContent = selected.dataset.planName;
        }

        this.renderPrice(selected);
        this.renderSubmitPrice(selected);
      }

      /**
       * Keeps the price in the Add to cart label on the same figure as the price
       * block. buy-buttons.liquid renders the one-time price, so it is written
       * back explicitly for one-time rather than left as whatever the last
       * subscription render put there. No-op when the label has no price, which
       * includes product cards.
       */
      renderSubmitPrice(selected) {
        const amount = this.form && this.form.querySelector('[data-submit-price-amount]');
        if (!amount) return;

        const planPrice = selected && selected.dataset.planPrice;
        const price = this.subscribed && planPrice ? planPrice : this.dataset.onetimePrice;
        if (price) amount.textContent = price;
      }

      /**
       * Rewrites the theme's price block to the selected plan's price.
       *
       * Always starts from the server-rendered markup, so every render is
       * computed from the original rather than from whatever the last one left
       * behind. Both the regular and the sale nodes are filled and .price--on-sale
       * decides which the theme shows, which is exactly how price.liquid drives
       * it — no display rules of our own.
       */
      renderPrice(selected) {
        if (!this.priceTarget) return;

        this.priceTarget.innerHTML = this.priceOriginalHTML;

        const planPrice = selected && selected.dataset.planPrice;
        if (!this.subscribed || !planPrice) return;

        const root = this.priceTarget.querySelector('.price');
        if (!root) return;

        const onetimePrice = this.dataset.onetimePrice;
        const discounted = Boolean(onetimePrice) && planPrice !== onetimePrice;

        root.classList.toggle('price--on-sale', discounted);

        // Child combinator, and `s` for the struck-through one: price.liquid gives
        // the compare-at and volume-pricing values the same .price-item--regular
        // class, but nests them one level deeper inside an <s>. A descendant
        // selector would find those first and overwrite the wrong number.
        const regular = root.querySelector('.price__regular > .price-item--regular');
        if (regular) regular.textContent = planPrice;

        const wasPrice = root.querySelector('.price__sale s.price-item--regular');
        if (wasPrice && discounted) wasPrice.textContent = onetimePrice;

        const salePrice = root.querySelector('.price__sale .price-item--sale');
        if (salePrice) salePrice.textContent = planPrice;

        this.renderSavingsBadge(root, discounted ? Number(selected.dataset.planSave) : 0);
      }

      /**
       * Fills the price block's "Save X%" badge with the selected plan's saving.
       *
       * The badge is server-rendered from the variant's compare at price, which
       * is a different offer from the subscription discount now being shown, so
       * whenever a plan price is in the block the badge has to state that plan's
       * saving instead. A plan at or above the one-time price leaves nothing to
       * claim, so the badge is hidden rather than left showing a stale figure.
       */
      renderSavingsBadge(root, percent) {
        const badge = root.querySelector('[data-price-savings]');
        if (!badge) return;

        const label = this.dataset.savingsLabel;
        if (!label || !Number.isFinite(percent) || percent <= 0) {
          badge.hidden = true;
          return;
        }

        badge.textContent = label.replace('[percent]', String(percent));
        badge.hidden = false;
      }
    }
  );
}
